import { useSnackbar } from 'notistack'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import InquiryComment from '~/features/inquiry/components/InquiryComment'
import ProccessStatus from '~/features/inquiry/components/ProccessStatus'
import {
  useInquiryDelete,
  useInquiryDetail,
} from '~/features/inquiry/queries/useInquiry'
import InquiryBgMobile from '~/shared/assets/background/inquiry-bg-mobile.png'
import InquiryBgPc from '~/shared/assets/background/inquiry-bg-pc.png'
import Back from '~/shared/assets/icons/Back'
import Delete from '~/shared/assets/icons/Delete'
import EditFIll from '~/shared/assets/icons/EditFIll'
import LoadingScreen from '~/shared/components/loading/LoadingScreen'
import useViewport from '~/shared/hooks/useViewport'
import theme from '~/shared/styles/theme'
import { fDate } from '~/shared/utils/format-date'

function replaceNewlinesWithBreaks(text: string) {
  return text.replace(/\n/g, '<br />')
}

function InquiryDetailPage() {
  const { enqueueSnackbar } = useSnackbar()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const viewport = useViewport()
  const isMobile = viewport < theme.deviceSizes.medium
  const backgroundImage = isMobile ? InquiryBgMobile : InquiryBgPc

  const { data, isLoading } = useInquiryDetail(id!)
  const { mutate: deleteInquiry } = useInquiryDelete()

  if (isLoading) return <LoadingScreen />

  const { info } = data!
  const { commentList } = info

  const handleClickEdit = () => {
    navigate(`/customer-service/inquiry/update`, { state: { info } })
  }

  const handleClickDelete = () => {
    const isConfirmed = window.confirm('정말로 삭제하시겠습니까?')
    if (isConfirmed) {
      deleteInquiry(info.id.toString(), {
        onSuccess: (res) => {
          if (res.errorCode === -6000) {
            enqueueSnackbar(res.errorMsg, { variant: 'error' })

            return
          }
          enqueueSnackbar('삭제되었습니다.', { variant: 'success' })
          navigate(-1)
        },
        onError: (error) => {
          enqueueSnackbar(error.message, { variant: 'error' })
        },
      })
    }
  }

  const handleOpenNewTab = (url: string) => {
    window.open(url, '_black', 'noopener noreferrer')
  }

  return (
    <Wrapper>
      <BackgroundImage src={backgroundImage} alt="background image" />
      <Container>
        <BackBox onClick={() => navigate('/customer-service/inquiry')}>
          <Back />
          <span className="text">돌아가기</span>
        </BackBox>
        <TitleBox>
          <TitleTag>{info.category_desc}</TitleTag>
          <Title>{info.title}</Title>
        </TitleBox>
        <Line />
        <SubTitleBox>
          <WriteDate>{`작성일 : ${fDate(info.updated_at, 'yyyy.MM.dd')}`}</WriteDate>
          {commentList && commentList.length === 0 && (
            <EditBox>
              <EditFIll />
              <EditText onClick={handleClickEdit}>수정하기</EditText>
            </EditBox>
          )}
        </SubTitleBox>
        {info.imageObj && (
          <Image
            src={info.imageObj.url}
            alt="inquiry"
            onClick={() => handleOpenNewTab(info.imageObj.url)}
          />
        )}
        <Body
          dangerouslySetInnerHTML={{
            __html: replaceNewlinesWithBreaks(info.body) || '',
          }}
        />
        <Line />
        <BottomBox>
          <ProccessStatus status={info.status} desc={info.status_desc} />
          <DeleteBox>
            <Delete color={theme.colors.warning} />
            <DeleteText onClick={handleClickDelete}>삭제하기</DeleteText>
          </DeleteBox>
        </BottomBox>
        {commentList &&
          commentList
            .reverse()
            .map((comment) => (
              <InquiryComment key={comment.id} comment={comment} />
            ))}
      </Container>
    </Wrapper>
  )
}

export default InquiryDetailPage

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: flex-start;

  box-sizing: border-box;

  /* width: fit-content; */
  width: 100%;
  max-width: 1080px;
  min-height: calc(100vh - ${theme.size.headerHeight}px);
  margin: 0 auto;
  margin-top: calc(80px + ${theme.size.headerHeight}px);
  margin-bottom: 120px;

  @media ${theme.device.medium} {
    gap: 24px;
    align-items: start;
    width: 90vw;
    margin-top: calc(56px + ${theme.size.headerHeight}px);
  }
`

const BackgroundImage = styled.img`
  position: absolute;
  z-index: ${theme.zIndex.belowBackground};
  top: 0;
  left: 0;

  overflow: hidden;

  width: 100%;
  height: 100%;

  object-fit: cover;
  background-repeat: no-repeat;

  @media ${theme.device.medium} {
    /* height: 100%; */
    background-repeat: repeat;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: start;

  box-sizing: border-box;
  width: 100%;
  padding: 36px 24px;

  background-color: white;
  border-radius: 12px;

  @media ${theme.device.medium} {
    gap: 24px;
    padding: 20px 24px;
  }
`

const BackBox = styled.div`
  cursor: pointer;
  display: flex;
  gap: 12px;
  align-items: center;

  .text {
    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 21px;
    color: ${theme.colors.grey_4};
  }
`

const TitleBox = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  width: 100%;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`

const TitleTag = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 100%;
  max-width: 164px;
  height: 41px;
  padding: 0 10px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
  color: #313634;

  background-color: #eaebeb;
  border-radius: 60px;

  @media ${theme.device.medium} {
    width: 100%;
    height: 37px;
    padding: 12px 8px;

    font-size: 15px;
    line-height: 21px;
  }
`

const Title = styled.h2`
  max-width: 900px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 700;
  line-height: 58px;
  color: #313634;

  @media ${theme.device.medium} {
    font-size: 28px;
    line-height: 39px;
  }
`

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: #c1c3c2;
`

const WriteDate = styled.span`
  font-size: 18px;
  font-weight: 500;
  line-height: 25px;
  color: #838685;

  @media ${theme.device.medium} {
    font-size: 15px;
    line-height: 21px;
  }
`

const Body = styled.div`
  @media ${theme.device.medium} {
    font-size: 15px;

    img,
    video {
      max-width: 100%;
      height: auto;
    }

    table {
      width: 100%;
    }

    pre,
    code {
      word-wrap: break-word;
      white-space: pre-wrap;
    }

    iframe {
      max-width: 100%;
    }
  }
`

const SubTitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const EditBox = styled.div`
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
`

const EditText = styled.span`
  font-size: 18px;
  font-weight: 500;
  line-height: 25px;
  color: #838685;

  @media ${theme.device.medium} {
    font-size: 15px;
    line-height: 21px;
  }
`

const DeleteBox = styled.div`
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
`

const DeleteText = styled.span`
  font-size: 18px;
  font-weight: 500;
  line-height: 25px;
  color: ${theme.colors.warning};

  @media ${theme.device.medium} {
    font-size: 15px;
    line-height: 21px;
  }
`

const Image = styled.img`
  cursor: pointer;

  display: block;

  max-width: 100%;
  height: auto;
  margin-bottom: 20px;
`

const BottomBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 8px;
  }
`
